import {explorerIsInformes} from '../../hooks/useReportModality';

/**
 * Las direcciones de Informes y las reglas de navegación del editor, en un solo lugar (card
 * 7yIKxqZj: fase 1 de la opción C del mockup del 02/10).
 *
 * En la 2.1 un informe vive en la ficha de SU cliente: `/client/:id/informes` es el editor con un
 * informe nuevo y `/client/:id/informes/:informeId`, ese informe. En el marco clásico la ficha no
 * tiene solapa Informes y la pantalla sigue siendo de agencia: `/informes?client_id=` y
 * `/informes/:informeId`, como antes. `/client/:id/reports` no sirve: es del reporte viejo con IA.
 *
 * Vive afuera de `InformesPage` porque es la parte que puede romperse sin que se vea: la pantalla
 * monta medio Explorador y no se puede afirmar nada sobre ella sin montarla entera.
 */

/**
 * La marca de una navegación del PROPIO editor: guardar, abrir otro informe, empezar uno nuevo o
 * cambiar de cliente desde el chip. Esas navegaciones ya pasan por el modal «Cambios sin guardar»
 * cuando corresponde, cambian la URL sin cambiar de pantalla y no pueden ni remontar el editor ni
 * disparar el aviso de salida. Viaja en `location.state` y sólo vale en un REPLACE: un «atrás»
 * que vuelve a esa entrada del historial trae el mismo `state`, pero es el usuario yéndose.
 */
export const INTERNAL_NAV_STATE = {informeInternal: true};

export function clientInformesPath(clientId, informeId = null) {
    const base = `/client/${clientId}/informes`;
    return informeId ? `${base}/${informeId}` : base;
}

export function agencyInformesPath(clientId, informeId = null) {
    if (informeId) return `/informes/${informeId}`;
    return `/informes?client_id=${encodeURIComponent(clientId || '')}`;
}

/** La dirección del editor sobre `clientId`: en la ficha (2.1) o en la pantalla de agencia. */
export function informesPathFor({clientScoped, clientId, informeId = null}) {
    if (clientScoped && clientId) return clientInformesPath(clientId, informeId);
    return agencyInformesPath(clientId, informeId);
}

/**
 * A dónde llevan «Informes» / «Explorar datos» desde la ficha de un cliente. Con la modalidad
 * Informes, a la solapa (en el clásico la ruta rebota sola a la pantalla de agencia); con la vieja,
 * al Explorador con el cliente en la query, como siempre.
 */
export function clientExplorerPath(clientId) {
    return explorerIsInformes() ? clientInformesPath(clientId) : `/data-explorer?client_id=${clientId}`;
}

export function isInternalNav(location, historyAction) {
    return historyAction === 'REPLACE' && !!(location && location.state && location.state.informeInternal);
}

/**
 * ¿Se frena esta salida para preguntar? Sólo con trabajo que se perdería, sólo si la navegación no
 * es del propio editor y sólo si cambia de pantalla: un clic en la solapa en la que uno ya está no
 * pierde nada.
 *
 * Nunca al cerrar sesión (review C-rec-fe-informes-01): el topbar borra la sesión ANTES de navegar a
 * `/login`, así que «Seguir editando» dejaba al usuario en un editor sin token, sin poder guardar y
 * sin topbar. Se mira el destino y, por si otro camino sale sin pasar por `/login`, la sesión.
 */
export function shouldBlockLeave({hasUnsavedWork, currentLocation, nextLocation, historyAction, hasSession = true}) {
    if (!hasUnsavedWork || !hasSession) return false;
    if (nextLocation.pathname === '/login') return false;
    if (isInternalNav(nextLocation, historyAction)) return false;
    return nextLocation.pathname !== currentLocation.pathname;
}

/**
 * ¿La página ya resolvió esta entrada? Las redirecciones de entrada (`informesRedirect`, el cliente
 * de `/informes/:informeId`) desmontan el editor, y sólo pueden correr cuando la página recibe una
 * navegación NUEVA de afuera: ésa ya pasó por el aviso de salida. Cambiar de marco (clásico ↔ 2.1)
 * no navega y el aviso no se entera: si se redirigiera ahí, el trabajo se perdía en silencio (review
 * C-rec-fe-informes-02). En ese caso el editor queda montado y corrige la URL él solo, con una
 * navegación interna. Las del propio editor tampoco se resuelven: ya nacen en la dirección del marco.
 */
export function entrySettled({shownAt, location, historyAction}) {
    if (shownAt === null || shownAt === undefined) return false;
    return shownAt === location.key || isInternalNav(location, historyAction);
}

/**
 * Qué instancia del editor corresponde a la URL. Las rutas de Informes montan el MISMO componente,
 * así que React lo conserva al pasar de una a otra: es lo que deja guardar un informe nuevo y seguir
 * en él con la URL nueva. Pero una navegación de AFUERA que cambia la dirección (la solapa estando
 * en un informe guardado, otro cliente desde la miga, «atrás») tiene que abrir lo que la URL dice,
 * y no dejar en pantalla el informe anterior debajo de una URL que ya no es la suya.
 */
export function nextEditorInstance(prev, {pathname, internal}) {
    if (prev.pathname === pathname) return prev;
    return {pathname, instance: internal ? prev.instance : prev.instance + 1};
}

/**
 * La redirección que corresponde antes de montar el editor, o `null`. `/informes/:informeId` en la
 * 2.1 no está acá: para saber de qué cliente es hay que pedir el informe (ver `InformesPage`).
 */
export function informesRedirect({ficha21, routeClientId, informeId, queryClientId}) {
    if (routeClientId) return ficha21 ? null : agencyInformesPath(routeClientId, informeId);
    if (ficha21 && !informeId && queryClientId) return clientInformesPath(queryClientId);
    return null;
}
